import { create } from "zustand";
import type { SessionUser } from "@webgis/shared";
import { load, save } from "../lib/storage";
import { translate, type Lang } from "../lib/i18n";

// ============================================================
// Global store — ported from design/prototype/store.jsx.
// Routing lives in react-router (URL); everything else here.
// ============================================================

export type Theme = "dark" | "light";

export interface Toast {
  id: string;
  type: "success" | "error" | "info" | "warn";
  msg: string;
  detail?: string;
  copy?: string;
  mono?: boolean;
  duration?: number;
}

export type DashLook = "atlas" | "bento" | "minimal";

export interface DashFilterEntry {
  layerId: string;
  clauses: Array<{ field: string; op: string; value: string }>;
  combine: "AND" | "OR";
  label: string;
  /** Arbeitskopie der Filterleiste — Form wie FilterState im FilterWorkshop */
  dash?: { districts: string[] | null; cats: string[]; noteBands: number[]; extra: Array<Record<string, unknown>> };
  at?: number;
}

interface StoreState {
  theme: Theme;
  lang: Lang;
  auth: SessionUser | null;
  /** true, sobald der Boot-Abgleich mit /auth/me abgeschlossen ist —
   *  vorher darf kein Guard auf app.* zum Anmelde-Host umleiten
   *  (localStorage ist je Origin leer, das Sitzungscookie aber gueltig). */
  authGeprueft: boolean;
  toasts: Toast[];
  dashLook: DashLook;
  mapLeftOpen: boolean;
  layerVis: Record<string, boolean> | null;
  /** user basemap pick keyed `projectId:theme` — overrides the admin's per-theme default */
  basemapChoice: Record<string, string>;
  /** collapsed layer groups in the map panel, keyed `${projectId}:${group}` */
  groupCollapsed: Record<string, boolean>;
  /** per-project layer ordering from panel drag & drop (top = topmost on map) */
  layerOrder: Record<string, string[]>;
  /** last project the user was in — so the topbar "Karte" button opens the right map */
  lastProjectId: string | null;
  /**
   * Aktiver Dashboard-Filter — JE PROJEKT eine Ablage. Die Karte bietet beim
   * Wechsel an, ihn zu uebernehmen; das Dashboard stellt ihn beim Oeffnen
   * wieder her. Ein einzelner Slot (die alte Form) verlor den Wiesbadener
   * Filter, sobald man kurz ins Cuxhavener Dashboard schaute.
   *
   * `dash` traegt die Arbeitskopie der Filterleiste (Ortsteile/Kategorien/
   * Noten/freie Bedingungen) — nur damit kommt der Filter als BEDIENBARE
   * Leiste zurueck, nicht als lose Klauselchips.
   */
  dashFilter: Record<string, DashFilterEntry | undefined>;
  /** zuletzt genutzter Dashboard-Tab je Projekt — der Rueckweg von der Karte landet dort */
  lastDashTab: Record<string, "charts" | "liste">;
  /**
   * Der Server liefert inzwischen eine andere Fassung aus als die, die hier
   * laeuft. Absichtlich NICHT gespeichert: nach einem Neuladen ist es vorbei.
   */
  updateReady: boolean;
  /**
   * Bildschirme mit ungespeicherten Aenderungen, nach Schluessel. Solange hier
   * etwas steht, wird NIE von selbst neu geladen — ein Entwurf im Feld ist
   * unwiederbringlich, eine veraltete Fassung dagegen bloss unbequem.
   */
  dirtyKeys: string[];

  setTheme(theme: Theme): void;
  toggleTheme(): void;
  setLang(lang: Lang): void;
  setAuth(auth: SessionUser | null): void;
  setAuthGeprueft(): void;
  setDashLook(look: DashLook): void;
  setMapLeftOpen(open: boolean): void;
  setLayerVis(vis: Record<string, boolean>): void;
  setBasemapChoice(projectId: string, theme: Theme, styleId: string): void;
  toggleGroupCollapsed(key: string): void;
  setLayerOrder(projectId: string, order: string[]): void;
  setLastProject(id: string): void;
  setDashFilter(projectId: string, f: DashFilterEntry | null): void;
  setLastDashTab(projectId: string, tab: "charts" | "liste"): void;
  setUpdateReady(ready: boolean): void;
  /** an/abmelden, dass dieser Bildschirm ungespeicherte Aenderungen hat */
  setDirty(key: string, dirty: boolean): void;
  addToast(toast: { msg: string } & Partial<Omit<Toast, "id" | "msg">>): string;
  removeToast(id: string): void;
}

function applyTheme(theme: Theme) {
  const el = document.documentElement;
  el.classList.toggle("dark", theme === "dark");
  el.classList.toggle("light", theme !== "dark");
  // Statusleistenfarbe (Android/PWA-Fenster) folgt dem gewaehlten Thema.
  // Werte = --bg aus tokens.css; als Literal, weil getComputedStyle hier vor
  // dem Stylesheet-Tausch den ALTEN Wert lesen wuerde.
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme === "dark" ? "#070707" : "#faf7f5");
}

export const useStore = create<StoreState>((set, get) => ({
  theme: load<Theme>("theme", "dark"),
  lang: load<Lang>("lang", "de"),
  auth: load<SessionUser | null>("auth", null),
  authGeprueft: false,
  toasts: [],
  dashLook: load<DashLook>("dashLook", "bento"),
  mapLeftOpen: true,
  layerVis: load<Record<string, boolean> | null>("layerVis", null),
  basemapChoice: load<Record<string, string>>("basemapChoice", {}),
  groupCollapsed: load<Record<string, boolean>>("groupCollapsed", {}),
  layerOrder: load<Record<string, string[]>>("layerOrder", {}),
  lastProjectId: load<string | null>("lastProject", null),
  updateReady: false,
  dirtyKeys: [],
  // Ueberlebt das Neuladen: die Karte wird oft NICHT ueber die Navigation
  // geoeffnet, sondern als frische Seite (Adresse getippt, neuer Tab, Handy nach
  // dem Wegsperren). Lag der Filter nur im Arbeitsspeicher, kam die Frage
  // „uebernehmen?" dort nie — der haeufigste Weg war der einzige, der nicht ging.
  // Nach 24 Stunden gilt er als veraltet: was gestern im Dashboard stand, will
  // heute niemand mehr ungefragt auf der Karte wiedersehen.
  dashFilter: (() => {
    const roh = load<unknown>("dashFilter", null);
    if (!roh || typeof roh !== "object") return {};
    const frisch = (e: DashFilterEntry | undefined) => (e && e.at && Date.now() - e.at < 24 * 3600_000 ? e : undefined);
    // Altbestand: die Ein-Slot-Form trug projectId im Eintrag selbst
    const alt = roh as DashFilterEntry & { projectId?: string };
    if (typeof alt.projectId === "string") {
      const e = frisch(alt);
      return e ? { [alt.projectId!]: e } : {};
    }
    const map = roh as Record<string, DashFilterEntry | undefined>;
    const out: Record<string, DashFilterEntry | undefined> = {};
    for (const [k, v] of Object.entries(map)) {
      const e = frisch(v);
      if (e) out[k] = e;
    }
    return out;
  })(),
  lastDashTab: load<Record<string, "charts" | "liste">>("lastDashTab", {}),

  setTheme(theme) {
    applyTheme(theme);
    save("theme", theme);
    set({ theme });
  },
  toggleTheme() {
    get().setTheme(get().theme === "dark" ? "light" : "dark");
  },
  setLang(lang) {
    save("lang", lang);
    set({ lang });
  },
  setAuthGeprueft() {
    set({ authGeprueft: true });
  },
  setAuth(auth) {
    save("auth", auth);
    set({ auth });
  },
  setUpdateReady(updateReady) {
    set({ updateReady });
  },
  setDirty(key, dirty) {
    set((st) => {
      const hat = st.dirtyKeys.includes(key);
      if (dirty === hat) return st; // nichts zu tun — kein neues Rendern ausloesen
      return { dirtyKeys: dirty ? [...st.dirtyKeys, key] : st.dirtyKeys.filter((k) => k !== key) };
    });
  },
  setDashLook(dashLook) {
    save("dashLook", dashLook);
    set({ dashLook });
  },
  setMapLeftOpen(mapLeftOpen) {
    set({ mapLeftOpen });
  },
  setLayerVis(layerVis) {
    save("layerVis", layerVis);
    set({ layerVis });
  },
  setBasemapChoice(projectId, theme, styleId) {
    // per-theme so a light pick doesn't override the admin's dark default (and vice versa)
    const basemapChoice = { ...get().basemapChoice, [`${projectId}:${theme}`]: styleId };
    save("basemapChoice", basemapChoice);
    set({ basemapChoice });
  },
  toggleGroupCollapsed(key) {
    const groupCollapsed = { ...get().groupCollapsed, [key]: !get().groupCollapsed[key] };
    save("groupCollapsed", groupCollapsed);
    set({ groupCollapsed });
  },
  setLayerOrder(projectId, order) {
    const layerOrder = { ...get().layerOrder, [projectId]: order };
    save("layerOrder", layerOrder);
    set({ layerOrder });
  },
  setLastProject(id) {
    if (!id || id === get().lastProjectId) return;
    save("lastProject", id);
    set({ lastProjectId: id });
  },
  setDashFilter(projectId, f) {
    if (!projectId) return;
    const cur = get().dashFilter;
    const next = { ...cur };
    if (f) next[projectId] = { ...f, at: Date.now() };
    else delete next[projectId];
    save("dashFilter", next);
    set({ dashFilter: next });
  },
  setLastDashTab(projectId, tab) {
    if (!projectId || get().lastDashTab[projectId] === tab) return;
    const lastDashTab = { ...get().lastDashTab, [projectId]: tab };
    save("lastDashTab", lastDashTab);
    set({ lastDashTab });
  },
  addToast(toast) {
    const id = Math.random().toString(36).slice(2);
    const base = { type: "info" as Toast["type"], ...toast };
    const isErr = base.type === "error";
    const t: Toast = {
      ...base,
      id,
      // errors linger (12s vs 3.6s) and always carry a copyable full message
      duration: base.duration ?? (isErr ? 12000 : 3600),
      copy: base.copy ?? (isErr ? [base.msg, base.detail].filter(Boolean).join("\n") : undefined),
    };
    set((s) => ({ toasts: [...s.toasts, t] }));
    if (t.duration !== 0) {
      setTimeout(() => get().removeToast(id), t.duration);
    }
    return id;
  },
  removeToast(id) {
    set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
  },
}));

// apply persisted theme on module load (index.html already pre-paints it)
applyTheme(useStore.getState().theme);

/** Translation hook bound to the current language. */
export function useT(): (key: string) => string {
  const lang = useStore((s) => s.lang);
  return (key) => translate(lang, key);
}

/** Initials from the display name — mirrors the prototype. */
export function useInitials(): string {
  const auth = useStore((s) => s.auth);
  if (!auth) return "";
  const parts = (auth.displayName || auth.username || "?").trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? "")).toUpperCase();
}

export function useIsAdmin(): boolean {
  return useStore((s) => s.auth?.role === "admin");
}
